import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Phone, Clock, Zap, TrendingUp, Bot, CheckCircle, Star, ArrowRight } from 'lucide-react';
import DemoModal from '@/components/DemoModal';

const Landing = () => {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  // Memoize background particles for smooth animation - More glowy strips
  const backgroundParticles = React.useMemo(() => {
    return [...Array(80)].map((_, i) => ({
      key: i,
      width: Math.random() * 3 + 1.5,
      height: Math.random() * 100 + 60,
      left: (i % 12) * 8.33 + Math.random() * 6,
      top: Math.floor(i / 12) * 15 + Math.random() * 12,
      rotation: Math.random() * 40 - 20,
      animationDelay: Math.random() * 15,
      animationDuration: Math.random() * 35 + 30,
      opacity: Math.random() * 0.12 + 0.03,
      glowIntensity: Math.random() * 0.3 + 0.1
    }));
  }, []);

  const stats = [
    { icon: Clock, label: '24/7', sublabel: 'Always Active' },
    { icon: Zap, label: '4x Faster', sublabel: 'Response Time' },
    { icon: TrendingUp, label: '+60%', sublabel: 'Conversion Rate' },
    { icon: Bot, label: 'AI-Powered', sublabel: 'Smart Scoring' }
  ];

  const benefits = [
    {
      title: 'Never Miss a Lead',
      description: 'AI qualification works 24/7. Capture and qualify prospects even while you sleep or vacation.'
    },
    {
      title: 'Save Time',
      description: 'Focus on high-potential clients. AI handles screening and eliminates unqualified prospects.'
    },
    {
      title: 'Increase Conversions',
      description: 'Reach qualified prospects instantly. AI prioritizes by buying intent and qualification.'
    },
    {
      title: 'Work Smarter',
      description: 'AI handles lead analysis with advanced machine learning algorithms.'
    }
  ];

  const features = [
    { 
      icon: TrendingUp,
      title: 'AI Lead Scoring', 
      description: 'ML analyzes behavior, demographics, and intent to score prospects 1-100.' 
    },
    { 
      icon: Zap,
      title: 'Instant Response', 
      description: 'Engage leads within 60 seconds via their preferred channel.' 
    },
    { 
      icon: Phone,
      title: 'Smart Qualification', 
      description: 'AI bot pre-qualifies leads 24/7 with real estate conversation flows.' 
    },
    { 
      icon: Bot,
      title: 'Market Intelligence', 
      description: 'Real-time data qualifies leads based on local property values.' 
    },
    { 
      icon: Star,
      title: 'Auto-Prioritization', 
      description: 'Sort leads by buying timeline, budget, and engagement.' 
    },
    { 
      icon: CheckCircle,
      title: 'Compliance Ready', 
      description: 'Built for North American regulations with full privacy compliance.' 
    }
  ];

  const testimonials = [
    {
      quote: 'PropFlo has been a game-changer for my real estate business. The AI qualification is incredibly accurate, and I\'ve closed 3 deals from leads that came in at 2 AM while I was sleeping. My conversion rate increased by 40% in just two months!',
      stat: '40% increase in conversions',
      name: 'Sarah Mitchell',
      title: 'Top Producer',
      brokerage: 'Century 21',
      location: 'Toronto, ON',
      deals: '47 deals in 2024',
      avatar: 'https://images.unsplash.com/photo-1627161683077-e34782c24d81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxyZWFsdG9yJTIwaGVhZHNob3R8ZW58MHx8fHwxNzYyNTc1MzgxfDA&ixlib=rb-4.1.0&q=85'
    },
    {
      quote: 'I was skeptical about AI at first, but PropFlo proved me wrong. The lead scoring is spot-on, and I\'m no longer wasting time on tire-kickers. I focus only on hot leads now, which has saved me 15+ hours per week.',
      stat: '60% time savings',
      name: 'Marcus Rodriguez',
      title: 'Licensed REALTOR®',
      brokerage: 'Keller Williams',
      location: 'Vancouver, BC',
      deals: '32 deals in 2024',
      avatar: 'https://images.unsplash.com/photo-1627161684850-52a7d958f8d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxyZWFsdG9yJTIwaGVhZHNob3R8ZW58MHx8fHwxNzYyNTc1MzgxfDA&ixlib=rb-4.1.0&q=85'
    },
    {
      quote: 'The voice agent handles initial calls so professionally that clients don\'t even realize it\'s AI until I tell them! Follow Up Boss integration is seamless, and every qualified lead lands directly in my CRM with complete notes.',
      stat: '95% lead capture rate',
      name: 'Jennifer Chen',
      title: 'Broker Associate',
      brokerage: 'RE/MAX',
      location: 'Calgary, AB',
      deals: '58 deals in 2024',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjI1NzUzODl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      quote: 'Before PropFlo, I was missing leads left and right. Now, every inquiry gets instant attention 24/7. My clients love the fast response time, and I love that I can focus on closing deals instead of answering the same questions repeatedly.',
      stat: '3x faster response time',
      name: 'David Thompson',
      title: 'Team Leader',
      brokerage: 'Royal LePage',
      location: 'Saint John, NB',
      deals: '41 deals in 2024',
      avatar: 'https://images.unsplash.com/photo-1627161684458-a62da52b51c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxyZWFsdG9yJTIwaGVhZHNob3R8ZW58MHx8fHwxNzYyNTc1MzgxfDA&ixlib=rb-4.1.0&q=85'
    },
    {
      quote: 'As a solo agent, I needed help managing my growing pipeline. PropFlo is like having a full-time assistant who never sleeps. The SMS and email automation alone has doubled my follow-up efficiency.',
      stat: '2x follow-up efficiency',
      name: 'Amanda Park',
      title: 'Independent REALTOR®',
      brokerage: 'Exit Realty',
      location: 'Moncton, NB',
      deals: '28 deals in 2024',
      avatar: 'https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg'
    },
    {
      quote: 'The ROI is insane. I pay less for PropFlo than hiring a part-time assistant, but get 10x more coverage. The AI asks better qualifying questions than most humans, and it syncs everything to FUB automatically.',
      stat: 'Exceptional ROI',
      name: 'Robert Williams',
      title: 'Broker of Record',
      brokerage: 'Sutton Group',
      location: 'Halifax, NS',
      deals: '67 deals in 2024',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjI1NzUzODl8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  const howItWorksSteps = [
    {
      step: 1,
      title: 'Lead Comes In',
      description: 'Potential buyers/sellers express interest via website, Zillow, Facebook, or any source.',
      details: ['Website', 'Social media', 'Zillow', 'Referrals']
    },
    {
      step: 2,
      title: 'AI Engages',
      description: 'Within 60 seconds, AI reaches out via phone, SMS, or email based on preferences.',
      details: ['Voice', 'SMS', 'Email', '60 seconds']
    },
    {
      step: 3,
      title: 'Smart Qualification',
      description: 'AI asks intelligent questions about intent, budget, location, timeline, and pre-approval.',
      details: ['Budget', 'Location', 'Timeline', 'Pre-approval']
    },
    {
      step: 4,
      title: 'Lead Scoring',
      description: 'AI analyzes conversation and assigns HOT, WARM, or COLD score based on factors.',
      details: ['HOT/WARM/COLD', 'Intent', 'Budget', 'Urgency']
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden scroll-smooth">
      {/* Enhanced Glowy Background Strips */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {backgroundParticles.map((particle) => (
          <div
            key={particle.key}
            className="absolute"
            style={{
              width: `${particle.width}px`,
              height: `${particle.height}px`,
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              transform: `rotate(${particle.rotation}deg)`,
              animation: `float ${particle.animationDuration}s ease-in-out infinite`,
              animationDelay: `${particle.animationDelay}s`,
              opacity: particle.opacity,
              background: `linear-gradient(to bottom, rgba(59, 130, 246, ${particle.glowIntensity}), rgba(147, 51, 234, ${particle.glowIntensity * 0.6}), transparent)`,
              filter: `blur(${particle.width > 2 ? '2px' : '1px'})`,
              boxShadow: `0 0 ${particle.width * 4}px rgba(59, 130, 246, ${particle.glowIntensity * 0.5})`
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav className="relative z-50 px-8 py-6 flex items-center justify-between border-b border-gray-800">
        <div className="flex items-center gap-3">
          <img 
            src="/propflo-logo.png" 
            alt="PropFlo" 
            className="h-32 w-auto"
          />
        </div>
        <div className="flex items-center gap-8">
          <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
          <button onClick={() => navigate('/pricing')} className="text-gray-300 hover:text-white transition-colors">Pricing</button>
          <a href="#testimonials" className="text-gray-300 hover:text-white transition-colors">Testimonials</a>
          <a href="#how-it-works" className="text-gray-300 hover:text-white transition-colors">How It Works</a>
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="text-white hover:text-gray-200"
          >
            Sign In
          </Button>
          <Button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-8 pt-24 pb-16 text-center">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            Qualify. Prioritise. Close.
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            AI-powered lead qualification for North American real estate professionals
          </p>

          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/login')}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 text-lg text-white shadow-lg shadow-blue-500/25 hover:shadow-2xl hover:shadow-blue-500/50 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </Button>
            <Button
              onClick={() => setDemoOpen(true)}
              size="lg"
              variant="outline"
              className="border-white/20 hover:border-blue-500/50 hover:bg-blue-500/10 px-8 py-4 text-lg text-white backdrop-blur-xl"
            >
              Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-10 px-8 pb-24">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="relative bg-black/40 border border-white/10 backdrop-blur-xl p-4 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/20 hover:scale-105 transition-all duration-500 group cursor-pointer overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10 flex flex-col items-center justify-center text-center">
                    <Icon className="w-6 h-6 text-blue-400 mb-2 group-hover:text-blue-300 group-hover:scale-110 transition-all duration-500" />
                    <div className="text-xl font-bold mb-1 text-white group-hover:text-blue-200 transition-colors duration-500">{stat.label}</div>
                    <div className="text-xs text-gray-200 group-hover:text-gray-100 transition-colors duration-500">{stat.sublabel}</div>
                  </div>
                  
                  <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500/0 group-hover:bg-blue-500/20 rounded-full blur-2xl transition-all duration-500"></div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative z-10 px-8 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm text-blue-400 uppercase tracking-wide mb-4">Transform Lead Management</h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-4 text-white">Focus on quality prospects with 24/7 AI</h3>
            <p className="text-xl text-gray-200">
              Stop wasting time on unqualified leads
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="relative bg-black/40 border border-white/10 backdrop-blur-xl p-8 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/20 hover:scale-105 transition-all duration-500 group cursor-pointer overflow-hidden h-full flex flex-col">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10 flex-1 flex flex-col">
                  <CheckCircle className="w-10 h-10 text-blue-400 mb-4 group-hover:text-blue-300 group-hover:scale-110 transition-all duration-500" />
                  <h4 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-200 transition-colors duration-500">{benefit.title}</h4>
                  <p className="text-gray-200 group-hover:text-gray-100 transition-colors duration-500 flex-1">{benefit.description}</p>
                </div>
                
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/0 group-hover:bg-blue-500/15 rounded-full blur-2xl transition-all duration-500 -translate-x-1/2 translate-y-1/2"></div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-8 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Qualify Leads Like a Pro
            </h3>
            <p className="text-xl text-gray-200">
              AI-powered tools for North American real estate professionals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="relative bg-black/40 border border-white/10 backdrop-blur-xl p-8 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/20 hover:scale-105 transition-all duration-500 group cursor-pointer overflow-hidden h-full flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent group-hover:via-blue-500/60 transition-all duration-500"></div>
                  
                  <div className="relative z-10 flex-1 flex flex-col">
                    <Icon className="w-10 h-10 text-blue-400 mb-4 group-hover:text-blue-300 group-hover:scale-110 transition-all duration-500" />
                    <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-200 transition-colors duration-500">{feature.title}</h4>
                    <p className="text-gray-200 text-base group-hover:text-gray-100 transition-colors duration-500 flex-1">{feature.description}</p>
                  </div>
                  
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-purple-500/0 group-hover:bg-purple-500/10 rounded-full blur-2xl transition-all duration-500"></div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Workflow Comparison */}
      <section className="relative z-10 px-8 py-20 bg-gradient-to-b from-gray-900/50 to-gray-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">Old Way</span>
              {' '}vs{' '}
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">New Way</span>
            </h3>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Save 15-20 minutes per lead on admin work. Focus on closing, not data entry.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Traditional Workflow */}
            <Card className="relative bg-black/40 border border-white/10 backdrop-blur-xl p-8 overflow-hidden hover:border-white/20 hover:shadow-xl hover:shadow-gray-500/10 transition-all duration-500 group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-500/5 to-transparent group-hover:from-gray-500/10 transition-all duration-500"></div>
              
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gray-500/30 to-transparent"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-gray-500/20 border border-gray-500/30 flex items-center justify-center backdrop-blur-sm">
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-200">Traditional Workflow</h4>
                </div>
                <ol className="space-y-4 text-gray-200">
                  <li className="flex gap-3 items-start">
                    <span className="text-gray-400 font-mono text-sm min-w-[20px] mt-0.5">01</span>
                    <span>Lead comes in</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-gray-400 font-mono text-sm min-w-[20px] mt-0.5">02</span>
                    <span>Realtor calls manually</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-gray-400 font-mono text-sm min-w-[20px] mt-0.5">03</span>
                    <span>Realtor manually types notes</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-gray-400 font-mono text-sm min-w-[20px] mt-0.5">04</span>
                    <span>Realtor sets reminders & adds to calendar</span>
                  </li>
                </ol>
                <div className="mt-6 p-4 bg-black/30 border border-white/10 rounded-lg backdrop-blur-sm">
                  <p className="text-gray-200 text-sm">
                    <span className="font-mono">⏱</span> <span className="text-white font-semibold">15-20 minutes per lead</span> wasted on admin
                  </p>
                </div>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gray-500/20 to-transparent"></div>
            </Card>

            {/* AIPropFlo Workflow */}
            <Card className="relative bg-black/40 border border-blue-500/30 backdrop-blur-xl p-8 overflow-hidden hover:border-blue-500/60 hover:shadow-2xl hover:shadow-blue-500/30 hover:scale-105 transition-all duration-500 group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent group-hover:from-blue-500/20 group-hover:via-purple-500/15 transition-all duration-500"></div>
              
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
              </div>
              
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/60 to-transparent"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/40 flex items-center justify-center backdrop-blur-sm shadow-lg shadow-blue-500/20">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-400 to-purple-400"></div>
                  </div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">AIPropFlo Workflow</h4>
                </div>
                <ol className="space-y-4 text-gray-100">
                  <li className="flex gap-3 items-start">
                    <span className="text-blue-400 font-mono text-sm min-w-[20px] mt-0.5">01</span>
                    <span><span className="text-blue-300 font-semibold">AI calls + qualifies</span> instantly</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-blue-400 font-mono text-sm min-w-[20px] mt-0.5">02</span>
                    <span><span className="text-blue-300 font-semibold">AI scores</span> lead and books appointment</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="text-blue-400 font-mono text-sm min-w-[20px] mt-0.5">03</span>
                    <div>
                      <span className="text-white font-semibold">You get notification:</span>
                      <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded backdrop-blur-sm text-sm">
                        <span className="text-blue-200">"Hot lead ready - meeting tomorrow 2pm"</span>
                      </div>
                    </div>
                  </li>
                </ol>
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg backdrop-blur-sm">
                  <p className="text-blue-200 text-sm font-semibold">
                    <span className="font-mono">⚡</span> <span className="text-white">30 seconds</span>—AI handles everything else
                  </p>
                </div>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
            </Card>
          </div>

          {/* ROI Calculator */}
          <div className="mt-12 text-center">
            <Card className="max-w-2xl mx-auto relative bg-black/40 border border-white/10 backdrop-blur-xl p-8 overflow-hidden hover:border-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/20 hover:scale-105 transition-all duration-500 group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent group-hover:from-blue-500/20 group-hover:via-purple-500/15 transition-all duration-500"></div>
              
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
              </div>
              
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
              
              <div className="relative z-10">
                <h5 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  ROI Calculator
                </h5>
                <p className="text-gray-200 text-lg mb-3">
                  20 leads/month?
                </p>
                <p className="text-white text-4xl font-bold mb-3">
                  Save <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">6+ hours</span> monthly
                </p>
                <p className="text-gray-200 text-sm">
                  Time you could spend closing deals
                </p>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 px-8 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
              How It Works
            </h3>
            <p className="text-xl text-gray-200">
              From lead capture to closing - automated, intelligent, and seamless
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative group">
                <Card className="relative h-full bg-black/40 border border-white/10 backdrop-blur-xl p-8 overflow-hidden hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 hover:scale-105 cursor-pointer flex flex-col">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent group-hover:via-blue-500/100 transition-all duration-500"></div>
                  
                  <div className="relative z-10 flex flex-col h-full text-center">
                    <div className="mb-6 flex justify-center">
                      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center backdrop-blur-sm group-hover:border-blue-500/60 group-hover:shadow-lg group-hover:shadow-blue-500/50 transition-all duration-500">
                        <span className="text-2xl font-bold bg-gradient-to-br from-blue-400 to-purple-400 bg-clip-text text-transparent">
                          {step.step}
                        </span>
                      </div>
                    </div>

                    <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors duration-300">
                      {step.title}
                    </h4>

                    <p className="text-gray-200 text-base mb-6 leading-relaxed flex-grow">
                      {step.description}
                    </p>

                    <div className="flex gap-2 flex-wrap justify-center mt-auto">
                      {step.details.map((detail, detailIndex) => (
                        <span 
                          key={detailIndex} 
                          className="px-3 py-1 text-xs bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-200 backdrop-blur-sm"
                        >
                          {detail}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent group-hover:via-purple-500/80 transition-all duration-500"></div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - FIXED SPACING */}
      <section id="testimonials" className="relative z-10 px-8 py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm text-blue-400 uppercase tracking-wide mb-4">Client Success Stories</h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Trusted by North America's Top Realtors
            </h3>
          </div>

          {/* Auto-scrolling testimonial strip with better spacing */}
          <div className="relative">
            <div className="flex gap-8 animate-scroll">
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <Card 
                  key={index} 
                  className="relative bg-black/40 border border-white/10 backdrop-blur-xl p-8 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-500 group cursor-pointer flex-shrink-0 w-[450px]"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent group-hover:via-blue-500/60 transition-all duration-500"></div>
                  
                  <div className="relative z-10">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-white text-base mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                    <div className="text-blue-300 font-semibold text-sm mb-6">{testimonial.stat}</div>
                    <div className="flex items-center gap-4">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-blue-500/30"
                      />
                      <div>
                        <div className="font-bold text-white text-base">{testimonial.name}</div>
                        <div className="text-sm text-gray-200">{testimonial.title}</div>
                        <div className="text-sm text-gray-300">{testimonial.location}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-8 py-32">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Ready to Qualify Leads on Autopilot?
          </h2>
          <p className="text-xl text-gray-200 mb-12">
            Join realtors who never miss a qualified lead
          </p>
          <Button
            onClick={() => navigate('/login')}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-12 py-6 text-lg text-white shadow-lg shadow-blue-500/25 hover:shadow-2xl hover:shadow-blue-500/50 hover:scale-105 transition-all duration-300"
          >
            Start 14-Day Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-200">
            <div>✓ 14-Day Free Trial</div>
            <div>✓ No Credit Card</div>
            <div>✓ 24/7 Support</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-8 py-12 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-gray-300">
              <p>© 2025 PropFlo. Built with Emergent AI Platform.</p>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a 
                href="/privacy" 
                className="text-gray-300 hover:text-blue-400 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/privacy');
                }}
              >
                Privacy Policy
              </a>
              <span className="text-gray-600">•</span>
              <a 
                href="/terms" 
                className="text-gray-300 hover:text-blue-400 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/terms');
                }}
              >
                Terms of Service
              </a>
              <span className="text-gray-600">•</span>
              <a 
                href="mailto:support@aipropflo.com" 
                className="text-gray-300 hover:text-blue-400 transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll {
          animation: scroll 40s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default Landing;
